import React, { useState, useEffect } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { executeAgent } from '../../services/agentService';
import AgentOutputActions from '../../components/Agents/AgentOutputActions';
import SecurityViolationModal from '../../components/Agents/SecurityViolationModal';
import { validateFields } from '../../utils/securityValidator';
import { formatElapsedTime } from '../../utils/timeUtils';
import { Step } from '../../types';
import { 
  AgentPageLayout, 
  AgentFormSection, 
  AgentFormGroup, 
  AgentLabel, 
  AgentInput, 
  AgentButton, 
  AgentError, 
  AgentResult,
  AgentDisclaimer
} from '../../components/Agents/AgentPageLayout';

const ContextResearcher: React.FC = () => {
  const [biblicalReference, setBiblicalReference] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [result, setResult] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [steps, setSteps] = useState<Step[]>([]);
  const [hasStarted, setHasStarted] = useState<boolean>(false);
  const [showSecurityModal, setShowSecurityModal] = useState<boolean>(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);

  useEffect(() => {
    if (!isLoading || startTime === null) return;
    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [isLoading, startTime]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!biblicalReference.trim()) {
      setError('Por favor, insira uma referência bíblica.');
      return;
    }

    // Validar segurança antes de enviar
    if (validateFields(biblicalReference)) {
      setShowSecurityModal(true);
      return;
    }

    setIsLoading(true);
    setError('');
    setResult('');
    setHasStarted(true);
    setStartTime(Date.now());
    setElapsedTime(0);
    setSteps([{ id: '1', title: 'Pesquisando contexto...', status: 'running' }]);

    try {
      const parameters = {
        prompt: `Pesquise informações históricas, culturais e literárias que iluminem o sentido original do texto da passagem: ${biblicalReference}`,
        passagem: biblicalReference
      };

      const data = await executeAgent('pesquisar-contexto', parameters);
      setResult(data.text);
      setSteps([{ id: '1', title: 'Contexto pesquisado', status: 'success' }]);
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao executar o agente';
      setError(`Erro: ${errorMessage}`);
      setSteps([{ id: '1', title: 'Erro ao pesquisar contexto', status: 'error', error: errorMessage }]);
      console.error('Erro ao executar agente:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setBiblicalReference('');
    setResult('');
    setError('');
    setSteps([]);
    setHasStarted(false);
  };

  return (
    <AgentPageLayout
      icon={<InformationCircleIcon className="w-6 h-6 text-secondary" />}
      title="Pesquisar Contexto"
      subtitle="Pesquise informações históricas, culturais e literárias que iluminem o sentido original do texto."
    >
      <AgentFormSection>
        <form onSubmit={handleSubmit} className="space-y-6">
          <AgentFormGroup>
            <AgentLabel htmlFor="biblicalReference">Referência Bíblica</AgentLabel>
            <AgentInput
              id="biblicalReference"
              type="text"
              value={biblicalReference}
              onChange={(e) => setBiblicalReference(e.target.value)}
              placeholder="Ex: Amós 1:1-2; Mateus 1.1"
              disabled={isLoading}
            />
          </AgentFormGroup>

          <div className="flex justify-end">
            <AgentButton type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Pesquisando Contexto...</span>
                </>
              ) : (
                <>
                  <InformationCircleIcon className="w-5 h-5" />
                  <span>Pesquisar Contexto</span>
                </>
              )}
            </AgentButton>
          </div>
        </form>

        {error && <AgentError message={error} />}

        {hasStarted && (
          <section className="mt-8">
            <div className="bg-card border border-border rounded-lg p-6">
              {isLoading && (
                <div className="flex flex-col items-center justify-center gap-4 py-8">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">😇</span>
                    <h3 className="text-lg font-semibold text-foreground">
                      Enquanto trabalhamos, eleve seu pensamento e descanse na presença de Deus.
                    </h3>
                  </div>
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
                  {steps.length > 0 && steps[0]?.title && (
                    <p className="text-sm text-muted-foreground">{steps[0].title}</p>
                  )}
                  {elapsedTime > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Tempo de execução: {formatElapsedTime(elapsedTime)}
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {result && (
          <AgentResult title="Contexto Pesquisado">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {result}
            </ReactMarkdown>
            <AgentDisclaimer />
            <div className="mt-6 flex items-center justify-end gap-3">
              <AgentOutputActions
                content={result}
                agentName="Pesquisar Contexto"
                agentCode="pesquisar-contexto"
                reference={biblicalReference}
              />
              <AgentButton
                type="button"
                onClick={handleReset}
                variant="secondary"
                aria-label="Reiniciar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                <span>Reiniciar</span>
              </AgentButton>
            </div>
          </AgentResult>
        )}
      </AgentFormSection>

      <SecurityViolationModal
        open={showSecurityModal}
        onClose={() => setShowSecurityModal(false)}
        onRestart={handleReset}
      />
    </AgentPageLayout>
  );
};

export default ContextResearcher;
